import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Building2, Smartphone, Users, Loader2 } from 'lucide-react';
import { api } from '../../services/api';

interface SearchResults {
  tenants: { id: string; name: string; subdomain: string; status: string }[];
  applications: { id: string; name: string; tenant_id: string; status: string }[];
  users: { id: string; email: string; name: string | null; role: string; tenant_id: string }[];
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const { data, isFetching } = useQuery<SearchResults>({
    queryKey: ['admin-search', debounced],
    queryFn: () => api.get(`/api/admin/search?q=${encodeURIComponent(debounced)}`),
    enabled: open && debounced.trim().length >= 2,
    staleTime: 30_000,
  });

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  if (!open) return null;

  const hasResults =
    data && (data.tenants.length > 0 || data.applications.length > 0 || data.users.length > 0);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-[15vh] bg-black/40"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 border-b border-gray-200">
          <Search className="w-5 h-5 text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && onClose()}
            placeholder="Search tenants, applications, users…"
            className="w-full py-3.5 text-sm bg-transparent outline-none border-0 focus:ring-0 text-gray-900"
          />
          {isFetching && <Loader2 className="w-4 h-4 text-gray-400 animate-spin shrink-0" />}
          <kbd className="hidden sm:block text-[10px] text-gray-400 border border-gray-200 rounded px-1.5 py-0.5 shrink-0">
            ESC
          </kbd>
        </div>

        <div className="max-h-80 overflow-y-auto scrollbar-thin">
          {debounced.trim().length < 2 ? (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">
              Type at least 2 characters to search the platform
            </p>
          ) : !hasResults && !isFetching ? (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">No results for “{debounced}”</p>
          ) : (
            <div className="py-2">
              {data?.tenants && data.tenants.length > 0 && (
                <Section label="Tenants">
                  {data.tenants.map((t) => (
                    <Row
                      key={t.id}
                      icon={Building2}
                      title={t.name}
                      subtitle={t.subdomain ? `${t.subdomain}.lumitalk.ai` : ''}
                      status={t.status}
                      onClick={() => go(`/tenants?tenant=${t.id}`)}
                    />
                  ))}
                </Section>
              )}
              {data?.applications && data.applications.length > 0 && (
                <Section label="Applications">
                  {data.applications.map((a) => (
                    <Row
                      key={a.id}
                      icon={Smartphone}
                      title={a.name}
                      subtitle=""
                      status={a.status}
                      onClick={() => go(`/tenants?tenant=${a.tenant_id}&app=${a.id}`)}
                    />
                  ))}
                </Section>
              )}
              {data?.users && data.users.length > 0 && (
                <Section label="Users">
                  {data.users.map((u) => (
                    <Row
                      key={u.id}
                      icon={Users}
                      title={u.name || u.email}
                      subtitle={u.name ? u.email : u.role}
                      status=""
                      onClick={() => go('/users')}
                    />
                  ))}
                </Section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-1">
      <p className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
        {label}
      </p>
      {children}
    </div>
  );
}

function Row({
  icon: Icon,
  title,
  subtitle,
  status,
  onClick,
}: {
  icon: React.ElementType;
  title: string;
  subtitle: string;
  status: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-primary-50 transition-colors"
    >
      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-gray-500" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
        {subtitle && <p className="text-xs text-gray-400 truncate">{subtitle}</p>}
      </div>
      {status && (
        <span
          className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${
            status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {status}
        </span>
      )}
    </button>
  );
}
